// ==============================|| VIDEO LINKS — WHAT AN AUTHOR PASTES, AND WHAT WE FRAME ||========= //
// An author pastes whatever their browser or the Share button gave them. This turns that into a
// provider and an id, and builds the embed address from a per-provider template.
//
// ⚠ THE PASTED STRING NEVER BECOMES THE FRAME'S ADDRESS, and that is the whole reason this module
// exists rather than a `src={fields.url}` in the component. An iframe src is a capability: whatever
// origin ends up there runs script inside our page's frame tree, on the origin that owns sign-in. So
// the flow is one-way — parse to `{provider, id}`, validate the id against a strict character class,
// then BUILD a URL out of a literal we wrote. Nothing an author types reaches the browser verbatim.
// `resolveHref` in QuickAccessSection applies the same shape of gate to link hrefs, for the same
// reason; this one is stricter because the payload is a frame rather than a navigation.
//
// ⚠ THE HOST IS MATCHED EXACTLY, NEVER BY SUFFIX. `endsWith('youtube.com')` also accepts
// `evil-youtube.com`, and `includes('youtube.com')` accepts `evil.com/?x=youtube.com`. Both are
// tested below. `new URL()` also settles the `https://www.youtube.com@evil.com` case for us — the
// host there IS `evil.com` — which is exactly why parsing beats a regex over the raw string.
//
// ⚠ IT LIVES IN api-client BECAUSE THREE CONSUMERS NEED THE SAME ANSWER: the console (to tell an
// author what it found as they type), the site renderer (to build the frame) and the tests. A copy in
// any one of them is a copy that can disagree with the CSP allowlist, which is the one place where
// disagreeing means a blank frame in production and nothing in dev.

/** The players we embed. Adding one here is also a change to `frame-src` in apps/login/csp.ts. */
export const siteVideoProviders = ['youtube', 'vimeo', 'dailymotion'] as const;
export type SiteVideoProvider = (typeof siteVideoProviders)[number];

/**
 * Hosts we accept a link FROM. Broader than the hosts we embed INTO: people paste from `m.youtube.com`
 * and from the share-sheet's `youtu.be`, and both mean a video we serve from `youtube-nocookie.com`.
 */
const HOSTS: Record<SiteVideoProvider, readonly string[]> = {
  youtube: ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'www.youtube-nocookie.com'],
  vimeo: ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'],
  dailymotion: ['dailymotion.com', 'www.dailymotion.com', 'dai.ly', 'geo.dailymotion.com']
};

/**
 * ⚠ THE ID GOES INTO A URL WE BUILD, so its character class is a security boundary rather than a
 * validation nicety. Each is the provider's documented shape and nothing wider — no dots, no slashes,
 * no percent signs, so an id can neither escape its path segment nor climb out of it.
 */
const ID_SHAPE: Record<SiteVideoProvider, RegExp> = {
  youtube: /^[A-Za-z0-9_-]{11}$/,
  vimeo: /^[0-9]{6,12}$/,
  dailymotion: /^[a-zA-Z0-9]{5,12}$/
};

/** A Vimeo unlisted link carries a hex token; anything else in that slot is dropped, not passed on. */
const VIMEO_HASH = /^[a-f0-9]{6,16}$/i;

/** The most a start offset may be — a day. Keeps a hostile `?t=` out of the built URL. */
const MAX_START_SECONDS = 86400;

export interface SiteVideoRef {
  provider: SiteVideoProvider;
  /** Validated against `ID_SHAPE`. Safe to interpolate; still URL-encoded at the point of use. */
  id: string;
  /** Vimeo's unlisted token, when the link carried a valid one. */
  hash: string | null;
  /** Seconds into the video, 0 when the link named none. */
  start: number;
}

function providerOf(hostname: string): SiteVideoProvider | null {
  const host = hostname.toLowerCase();
  for (const provider of siteVideoProviders) if (HOSTS[provider].includes(host)) return provider;
  return null;
}

/** `90`, `1m30s`, `2h3m4s` — the three shapes a share link uses. Anything else is no offset. */
function parseStart(raw: string | null): number {
  if (!raw) return 0;
  if (/^[0-9]+$/.test(raw)) return Math.min(Number(raw), MAX_START_SECONDS);
  const parts = raw.match(/^(?:([0-9]+)h)?(?:([0-9]+)m)?(?:([0-9]+)s)?$/);
  if (!parts || parts[0] === '') return 0;
  const seconds = Number(parts[1] ?? 0) * 3600 + Number(parts[2] ?? 0) * 60 + Number(parts[3] ?? 0);
  return Math.min(seconds, MAX_START_SECONDS);
}

/**
 * What an author pasted, or `null` if it is not a video link we can frame.
 *
 * ⚠ NULL IS A FULL ANSWER, NOT AN ERROR CASE TO PATCH AROUND. The console says so in words and the
 * renderer draws nothing — the same contract `resolveHref` has. A section whose link does not parse
 * renders no frame at all rather than an empty box.
 */
export function parseVideoUrl(raw: string): SiteVideoRef | null {
  const text = raw.trim();
  if (!text) return null;

  let url: URL;
  try {
    url = new URL(text);
  } catch {
    // Not absolute, so not a video link. A protocol-relative `//youtube.com/...` lands here too, which
    // is the intent: it would inherit our scheme and read as first-party.
    return null;
  }

  // `javascript:` and `data:` cannot reach the branches below, but refusing them by name here means the
  // refusal does not depend on every branch staying careful.
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const provider = providerOf(url.hostname);
  if (!provider) return null;

  const segments = url.pathname.split('/').filter(Boolean);
  // ⚠ KEYWORDS MATCH CASE-INSENSITIVELY, IDS NEVER DO. A link that has been through a mail client or a
  // word processor's autocapitalise arrives as `/WATCH?V=...`, and refusing that is a support report
  // nobody can diagnose from the outside. The id keeps its own case because YouTube ids are
  // case-sensitive — `dQw4w9WgXcQ` and `dqw4w9wgxcq` are different videos.
  const keyword = segments.map((segment) => segment.toLowerCase());
  const param = (name: string): string | null => {
    let found: string | null = null;
    url.searchParams.forEach((value, key) => {
      if (key.toLowerCase() === name) found = value;
    });
    return found;
  };

  let id: string | null = null;
  let hash: string | null = null;

  if (provider === 'youtube') {
    if (url.hostname.toLowerCase() === 'youtu.be') id = segments[0] ?? null;
    else if (keyword[0] === 'embed' || keyword[0] === 'shorts' || keyword[0] === 'live') id = segments[1] ?? null;
    else if (keyword[0] === 'watch') id = param('v');
  } else if (provider === 'vimeo') {
    if (keyword[0] === 'video') {
      id = segments[1] ?? null;
    } else {
      id = segments[0] ?? null;
      hash = segments[1] ?? null;
    }
    hash = param('h') ?? hash;
    if (hash !== null && !VIMEO_HASH.test(hash)) hash = null;
  } else {
    if (url.hostname.toLowerCase() === 'dai.ly') id = segments[0] ?? null;
    else if (keyword[0] === 'video') id = segments[1] ?? null;
    else id = param('video');
  }

  if (id === null || !ID_SHAPE[provider].test(id)) return null;

  return { provider, id, hash, start: parseStart(param('t') ?? param('start')) };
}

/**
 * The address the frame actually loads — built here, from literals.
 *
 * ⚠ THE ORIGINS BELOW MUST MATCH `frame-src` IN apps/login/csp.ts EXACTLY. A player added here and not
 * there is a frame the browser refuses with a console violation and nothing on the page;
 * `siteVideo.test.ts` asserts the pair so the two cannot drift apart silently.
 *
 * ⚠ YOUTUBE IS SERVED FROM `youtube-nocookie.com`, NOT `youtube.com`, even though we accept links from
 * both. It is the same player without the cookie the normal domain sets on load, which matters on a
 * site whose visitors are school pupils. `rel=0` keeps the end-card suggestions inside the channel.
 */
export function siteVideoEmbedSrc(video: SiteVideoRef, autoplay = false): string {
  const query = new URLSearchParams();

  if (video.provider === 'youtube') {
    query.set('rel', '0');
    query.set('modestbranding', '1');
    if (video.start > 0) query.set('start', String(video.start));
    if (autoplay) query.set('autoplay', '1');
    return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.id)}?${query}`;
  }

  if (video.provider === 'vimeo') {
    if (video.hash !== null) query.set('h', video.hash);
    if (autoplay) query.set('autoplay', '1');
    // Vimeo takes the offset as a fragment, not a query parameter.
    const fragment = video.start > 0 ? `#t=${video.start}s` : '';
    return `https://player.vimeo.com/video/${encodeURIComponent(video.id)}?${query}${fragment}`;
  }

  query.set('video', video.id);
  if (video.start > 0) query.set('startTime', String(video.start));
  if (autoplay) query.set('autoplay', '1');
  return `https://geo.dailymotion.com/player.html?${query}`;
}

/**
 * The origins a frame may be loaded from — the CSP's `frame-src` allowlist, derived from the same
 * table the embed builder uses so the policy cannot fall behind the players.
 */
export const siteVideoFrameOrigins: readonly string[] = [
  'https://www.youtube-nocookie.com',
  'https://player.vimeo.com',
  'https://geo.dailymotion.com'
];

/** Display name for a provider — a proper noun, so it is not translated. */
export function siteVideoProviderName(provider: SiteVideoProvider): string {
  return { youtube: 'YouTube', vimeo: 'Vimeo', dailymotion: 'Dailymotion' }[provider];
}
